import { Item as DropdownMenuItem } from '@radix-ui/react-dropdown-menu';
import { Fragment, useState } from 'react';

import { ButtonDropdown } from '@/components/core/ButtonDropdown/ButtonDropdown';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/core/DropdownMenu/DropdownMenu';
import { useTranslation } from '@/hooks/useTranslation';

import { FilterAttributeOption, FilterGroup, FilterLogicalOperator, FilterRule } from './FilterBuilder.types';
import { createEmptyRule } from './filterGroups';
import { RuleRow } from './RuleRow';

interface GroupBlockProps {
    group: FilterGroup;
    attributes: FilterAttributeOption[];
    onChange: (group: FilterGroup) => void;
}

const LOGICAL_OPERATORS: FilterLogicalOperator[] = ['AND', 'OR'];

const LogicalOperatorSelect = ({
    value,
    onChange,
    ariaLabel,
}: {
    value: FilterLogicalOperator;
    onChange: (operator: FilterLogicalOperator) => void;
    ariaLabel: string;
}) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const label = value === 'AND' ? t('ITEMS_SEARCH_FILTER.OPERATOR_AND') : t('ITEMS_SEARCH_FILTER.OPERATOR_OR');

    return (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <DropdownMenuTrigger asChild={true}>
                <ButtonDropdown
                    titleOne={label}
                    isOpen={isOpen}
                    aria-label={ariaLabel}
                />
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="start"
                hasBorder={true}
                hasPadding={false}
                className="tw-min-w-[5rem]"
            >
                {LOGICAL_OPERATORS.map((operator) => (
                    <DropdownMenuItem
                        key={operator}
                        className="tw-flex tw-items-center tw-px-3 tw-py-2 tw-text-t-sm tw-cursor-pointer tw-outline-none data-[highlighted]:tw-bg-fill-primary-hover hover:tw-bg-fill-primary-hover"
                        onSelect={() => onChange(operator)}
                    >
                        {operator === 'AND' ? t('ITEMS_SEARCH_FILTER.OPERATOR_AND') : t('ITEMS_SEARCH_FILTER.OPERATOR_OR')}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

const GroupBlock = ({ group, attributes, onChange }: GroupBlockProps) => {
    const { t } = useTranslation();
    const showRuleConnector = group.rules.length > 1;

    const updateRule = (index: number, nextRule: FilterRule) => {
        const rules = group.rules.map((rule, ruleIndex) => (ruleIndex === index ? nextRule : rule));
        onChange({ ...group, rules });
    };

    const addRule = (index: number) => {
        const rules = [...group.rules];
        rules.splice(index + 1, 0, { ...createEmptyRule(), connector: group.ruleConnector });
        onChange({ ...group, rules });
    };

    const removeRule = (index: number) => {
        onChange({ ...group, rules: group.rules.filter((_, ruleIndex) => ruleIndex !== index) });
    };

    const setRuleConnector = (ruleConnector: FilterLogicalOperator) => {
        const rules = group.rules.map((rule, index) => (
            index === 0 ? rule : { ...rule, connector: ruleConnector }
        ));
        onChange({ ...group, ruleConnector, rules });
    };

    return (
        <div className="tw-flex tw-flex-col tw-gap-5 tw-items-stretch tw-w-full tw-min-w-0 tw-bg-fill-secondary tw-rounded-lg tw-px-5 tw-py-4 lg:tw-flex-row lg:tw-items-center">
            <div className="tw-hidden lg:tw-flex tw-shrink-0 tw-items-center">
                {showRuleConnector ? (
                    <LogicalOperatorSelect
                        value={group.ruleConnector}
                        onChange={setRuleConnector}
                        ariaLabel={t('ITEMS_SEARCH_FILTER.OPERATOR_AND')}
                    />
                ) : (
                    <div className="tw-invisible tw-pointer-events-none" aria-hidden={true}>
                        <LogicalOperatorSelect
                            value="AND"
                            onChange={() => undefined}
                            ariaLabel=""
                        />
                    </div>
                )}
            </div>
            <div className="tw-flex tw-flex-col tw-gap-8 tw-items-stretch tw-w-full tw-min-w-0 tw-flex-1">
                {group.rules.map((rule, index) => (
                    <Fragment key={index}>
                        {index > 0 && (
                            <div className="lg:tw-hidden tw-flex tw-flex-col tw-items-start tw-shrink-0">
                                <LogicalOperatorSelect
                                    value={group.ruleConnector}
                                    onChange={setRuleConnector}
                                    ariaLabel={t('ITEMS_SEARCH_FILTER.OPERATOR_AND')}
                                />
                            </div>
                        )}
                        <RuleRow
                            rule={rule}
                            attributes={attributes}
                            onChange={(nextRule) => updateRule(index, nextRule)}
                            onRemove={() => removeRule(index)}
                            onAdd={() => addRule(index)}
                        />
                    </Fragment>
                ))}
            </div>
        </div>
    );
};

GroupBlock.displayName = 'GroupBlock';

export { GroupBlock, LogicalOperatorSelect };
